import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const VerifySignature = () => {
  const { signatureId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [fileState, setFileState] = useState('idle');
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    axios
      .get(`/api/documents/verify-public/${signatureId}`)
      .then(res => setResult(res.data.data))
      .catch(() => setResult({ isValid: false, reason: 'error' }))
      .finally(() => setLoading(false));
  }, [signatureId]);

  const formatDate = iso =>
    new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  const shortHash = hash =>
    hash ? `${hash.slice(0, 8)}…${hash.slice(-8)}` : '';

  const copyHash = () => {
    navigator.clipboard.writeText(result.documentHash)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); })
      .catch(() => {});
  };

  const computeAndCompare = useCallback(async (file) => {
    if (!file || file.type !== 'application/pdf') {
      setFileState('not_pdf');
      return;
    }
    setFileState('computing');
    try {
      const buffer = await file.arrayBuffer();
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
      const hashHex = Array.from(new Uint8Array(hashBuffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      setFileState(hashHex === result.documentHash ? 'match' : 'mismatch');
    } catch {
      setFileState('idle');
    }
  }, [result]);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    computeAndCompare(e.dataTransfer.files[0]);
  }, [computeAndCompare]);

  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false);
  };
  const onFileChange = (e) => computeAndCompare(e.target.files[0]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500 text-sm">Vérification en cours...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="text-lg font-bold text-gray-800">Vérification de Signature</h1>
          <p className="text-xs text-gray-400 mt-1">Tchin-Lait — PKI Interne</p>
        </div>

        {result?.isValid ? (
          <div className="bg-green-50 border border-green-200 rounded-xl p-5">
            <div className="flex items-start gap-3 mb-4">
              <span className="text-2xl">✅</span>
              <div>
                <p className="font-semibold text-green-800">Signature valide</p>
                {result.migrated && (
                  <p className="text-xs text-green-600 mt-0.5">
                    Document migré — original du {formatDate(result.originalSignedAt)}
                  </p>
                )}
              </div>
            </div>

            <div className="text-sm text-gray-700 space-y-1.5">
              <div className="flex justify-between">
                <span className="text-gray-500">Signé par</span>
                <span className="font-medium">{result.signerName}</span>
              </div>
              {result.signerRole && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Rôle</span>
                  <span>{result.signerRole}</span>
                </div>
              )}
              {result.signerDepartment && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Département</span>
                  <span>{result.signerDepartment}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Date</span>
                <span>{formatDate(result.signedAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Type</span>
                <span className="capitalize">{result.documentType}</span>
              </div>
            </div>

            {result.documentHash && (
              <>
                <div className="border-t border-green-200 mt-4 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">Empreinte SHA-256</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-gray-600">
                        {shortHash(result.documentHash)}
                      </span>
                      <button
                        onClick={copyHash}
                        className="text-xs text-green-700 hover:text-green-900 underline"
                      >
                        {copied ? 'Copié !' : 'Copier'}
                      </button>
                    </div>
                  </div>
                </div>

                <div
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onClick={() => inputRef.current?.click()}
                  className={`mt-4 border-2 border-dashed rounded-xl p-4 text-center cursor-pointer transition-colors ${
                    dragging
                      ? 'border-green-400 bg-green-100'
                      : 'border-green-300 hover:border-green-400 hover:bg-green-50'
                  }`}
                >
                  <input
                    ref={inputRef}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={onFileChange}
                  />
                  {fileState === 'idle' && (
                    <p className="text-xs text-gray-500">
                      Déposez votre PDF ici pour vérifier qu'il n'a pas été modifié
                    </p>
                  )}
                  {fileState === 'computing' && (
                    <p className="text-xs text-gray-500">Calcul en cours…</p>
                  )}
                  {fileState === 'match' && (
                    <p className="text-xs font-medium text-green-700">
                      ✅ Fichier authentique — ce document n'a pas été modifié
                    </p>
                  )}
                  {fileState === 'mismatch' && (
                    <p className="text-xs font-medium text-red-700">
                      ❌ Fichier modifié — ce document ne correspond pas à la version signée
                    </p>
                  )}
                  {fileState === 'not_pdf' && (
                    <p className="text-xs font-medium text-orange-600">
                      Format non supporté — déposez un fichier PDF
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <span className="text-2xl">❌</span>
              <div>
                <p className="font-semibold text-red-800">
                  {result?.reason === 'file_missing'
                    ? 'Fichier introuvable sur le serveur'
                    : result?.reason === 'no_signature'
                    ? 'Ce document ne contient pas de signature'
                    : 'Signature invalide — document potentiellement modifié'}
                </p>
                <p className="text-xs text-red-600 mt-1">
                  Ce document ne peut pas être certifié authentique.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifySignature;
